import {
    type AdjustSearchIndexAction,
    type SearchChangeAction,
    type SellScreenActionTypes,
} from "../../Actions/SellScreen/SellScreenActions";
import {
    SALE_REQUEST_ASSOCIATE_BARCODE,
    SALE_UPDATE_PRODUCTS,
    SELL_SCREEN_ACTIVE_PRODUCT_INDEX,
    SELL_SCREEN_CHANGE_INVALID_INDEXES,
    SELL_SCREEN_CHANGE_RESULTS_TO,
    SELL_SCREEN_NEXT_SEARCH_ACTION,
    SELL_SCREEN_RESET_SEARCH_CARET,
    SELL_SCREEN_SEARCH_ADJUST_INDEX,
    SELL_SCREEN_SEARCH_ADJUST_RESULT,
    SELL_SCREEN_SEARCH_CHANGE,
    SELL_SCREEN_SEARCH_REPLACE,
    SELL_SCREEN_SEARCH_RESULTS_LENGTH,
    SELL_SCREEN_SET_SEARCHING,
} from "../../Actions/SellScreen/SellScreenConstants";
import { type ShopfrontActions } from "../../Utilities/Generic/ActionsHelper";

export interface SearchState {
    value: string;
    caret: boolean | number;
    index: false | number;
    invalid: Array<number>;
    searching: boolean;
    selected: {
        kind: string;
        uuid: string | false;
    };
    associate: {
        enabled: boolean;
        barcode: string;
    };
    count: number;
    active: number;
    to: string;
    nextAction: boolean | string;
}

function changeSearch(
    state: SearchState,
    action: SearchChangeAction
): SearchState {
    let value;
    let caret: false | number = false;

    // Check if the value has special meaning
    if(action.value.length > 1 && action.value.charAt(0) === "[") {
        if(action.value === "[Backspace]" || action.value === "[Delete]") {
            if(typeof action.additional !== "undefined" && action.additional.start !== false) {
                if(!action.additional.end) {
                    // Set the selection
                    if(action.value === "[Backspace]") {
                        // Remove backwards
                        action.additional.end = action.additional.start;
                        action.additional.start -= 1;
                    } else {
                        // Remove forwards
                        action.additional.end = action.additional.start + 1;
                    }
                }

                // Remove between the selection
                value = state.value.substr(0, action.additional.start) + state.value.substr(action.additional.end);
                caret = action.additional.start;
            } else if(action.value === "[Backspace]") {
                value = state.value.slice(0, -1);
            } else {
                value = state.value;
            }
        } else if(action.value === "[Clear]") {
            value = "";
        } else {
            // Invalid special meaning
            console.error("Special search action is not implemented", state.value, action.value);
            value = state.value + action.value.toString();
        }
    } else {
        // No special meaning
        if(typeof action.additional !== "undefined" && action.additional.start !== false) {
            if(action.additional.end !== false && action.additional.start !== action.additional.end) {
                // Remove the characters between start and end
                value = state.value.substr(0, action.additional.start) + state.value.substr(action.additional.end);
            } else {
                value = state.value;
            }

            // Insert the characters at the start
            value = value.substr(0, action.additional.start) +
                action.value.toString() +
                value.substr(action.additional.start);

            caret = action.additional.start + 1;
        } else {
            value = state.value + action.value.toString();
        }
    }

    if(caret !== false && caret < 0) {
        caret = 0;
    }

    value = value.trimStart();

    return {
        ...state,
        value,
        caret,
        invalid: [],
    };
}

function adjustIndex(
    state: SearchState,
    action: AdjustSearchIndexAction
): SearchState {
    let index: false | number = state.index;

    if(action.amount === false) {
        index = false;
    } else if(action.absolute) {
        index = action.amount;
    } else {
        (index as number) += action.amount;
    }

    return {
        ...state,
        index,
    };
}

function SellScreenSearchReducer(
    state: SearchState | undefined,
    action: ShopfrontActions<SellScreenActionTypes>
): SearchState {
    if(typeof state === "undefined") {
        return {
            value    : "",
            caret    : false,
            index    : false,
            searching: false,
            invalid  : [],
            selected : {
                kind: "product",
                uuid: false,
            },
            associate: {
                enabled: false,
                barcode: "",
            },
            count     : 0,
            active    : 0,
            to        : "sale",
            nextAction: false,
        };
    }

    switch(action.type) {
        case SELL_SCREEN_SEARCH_CHANGE:
            return changeSearch(state, action);
        case SELL_SCREEN_SET_SEARCHING:
            return {
                ...state,
                searching: action.searching,
            };
        case SELL_SCREEN_SEARCH_REPLACE:
            return {
                ...state,
                value: action.value.trimStart(),
                caret: action.caret,
            };
        case SELL_SCREEN_RESET_SEARCH_CARET:
            return {
                ...state,
                caret: false,
            };
        case SELL_SCREEN_CHANGE_INVALID_INDEXES:
            return {
                ...state,
                invalid: action.invalid,
            };
        case SELL_SCREEN_SEARCH_ADJUST_INDEX:
            return adjustIndex(state, action);
        case SELL_SCREEN_SEARCH_RESULTS_LENGTH:
            return {
                ...state,
                searching: false,
                count    : action.amount,
            };
        case SELL_SCREEN_SEARCH_ADJUST_RESULT:
            return {
                ...state,
                selected: {
                    ...state.selected,
                    kind: action.kind,
                    uuid: action.uuid,
                },
            };
        case SALE_REQUEST_ASSOCIATE_BARCODE:
            return {
                ...state,
                associate: {
                    enabled: action.enabled,
                    barcode: action.barcode ? action.barcode : "",
                },
            };
        case SELL_SCREEN_ACTIVE_PRODUCT_INDEX:
            return {
                ...state,
                active: action.absolute ? action.amount : state.active + action.amount,
            };
        case SELL_SCREEN_CHANGE_RESULTS_TO:
            return {
                ...state,
                to: action.to,
            };
        case SELL_SCREEN_NEXT_SEARCH_ACTION:
            return {
                ...state,
                nextAction: action.action,
            };
        case SALE_UPDATE_PRODUCTS:
            return {
                ...state,
                nextAction: false,
            };
        default:
            return state;
    }
}

export default SellScreenSearchReducer;
